import { useEffect, useMemo } from "react"

import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import Button from "../../../../Components/Button"
import EmptyComponent from "../../../../Components/Empty"
import Table from "../../../../Components/Table"
import { RoutesPaths } from "../../../../constants/user-routes"
import Icon from "../../../../Icon"
import { ANALYTICS_SECTION } from "../../redux/analytics.constants"
import { selectSectionData, selectSectionFetching } from "../../redux/analytics.slice"
import { buildColumns } from "../activityColumns"
import Section from "../Section"

import tables from "../../tables.module.less"
import styles from "./activityStream.module.less"

// How many events the card feed shows; "View all" opens the full activity page.
const VISIBLE = 10

// "Activity stream": a feed of the most recent dispute events (newest first), several per
// dispute (arrival, evidence, submission, outcome). The dispute columns (#, status, customer,
// amount, category) render exactly like the /recover/all table; "What changed" and "When" are
// the feed's own. A "View all" header link (same as the Top countries card) opens the full
// activity page.
const ActivityStream = ({ getData }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const activity = useSelector(selectSectionData(ANALYTICS_SECTION.ACTIVITY))
  const fetching = useSelector(selectSectionFetching(ANALYTICS_SECTION.ACTIVITY))
  const rows = activity?.results ?? []

  useEffect(() => {
    getData(ANALYTICS_SECTION.ACTIVITY, { page_size: VISIBLE })
  }, [getData])

  const columns = useMemo(() => buildColumns(t), [t])

  // Every row is an event on one dispute, so the row opens that dispute, exactly as a row
  // in the /recover/all table does. The cells that already carry their own action (the
  // customer link, the email copy button) stop the click from bubbling, so they still do
  // their own thing.
  const openDispute = (row) => ({
    onClick: () => navigate(RoutesPaths.recover.details(row.dispute_id)),
    style: { cursor: "pointer" }
  })

  const viewAll = (
    <Button
      type="link"
      onClick={() => navigate(RoutesPaths.recover.analytics_activity)}
      icon={<Icon type="chevron_right_small" />}
      iconPosition="end"
      style={{ padding: 0 }}
      size="small"
      text={t("View All")}
    />
  )

  return (
    <Section title={t("Activity stream")} extra={rows.length ? viewAll : undefined}>
      {rows.length || fetching ? (
        <div className={tables.rowHover}>
          <Table
            // Flat header, matching the Repeat disputers card next to it: the shared table's
            // boxed grey header belongs on the full data tables, not on a card feed.
            className={styles.flatHeader}
            dataSource={rows}
            columns={columns}
            rowKey="event_id"
            loading={fetching}
            withOutPagination
            scroll={{}}
            onRow={openDispute}
          />
        </div>
      ) : (
        <EmptyComponent type="no_data_available" />
      )}
    </Section>
  )
}

export default ActivityStream
